import { Box, Slider } from "@mui/material";
import { SyntheticEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { orthoCamActions, selectCrossSectionClipping } from "features/orthoCam";
import { useUom } from "features/uom/hooks/useUom";
import { UnitType } from "features/uom/spec";
import { convertAndFormat } from "features/uom/utils";

/**
 * ClippingPlaneFarSlider component allows users to adjust the far clipping plane of an orthographic camera.
 * @param props.opaqueBackground - If true, the background of the label will be opaque.
 * @param props.relative - If true, the slider will be positioned relative to its parent container. Otherwise absolute positioning is used.
 */
export function ClippingPlaneFarSlider({
    opaqueBackground,
    relative,
}: {
    opaqueBackground?: boolean;
    relative?: boolean;
}) {
    const {
        state: { view },
    } = useExplorerGlobals();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const uom = useUom();

    const _clipping = useAppSelector(selectCrossSectionClipping);

    const [clipping, setClipping] = useState(_clipping);

    useEffect(() => setClipping(_clipping), [_clipping]);

    if (!view) {
        return null;
    }

    const handleClippingChange = (_event: Event, newValue: number | number[]) => {
        if (Array.isArray(newValue)) {
            return;
        }

        setClipping(newValue);
        if (view.renderState.camera.kind === "orthographic") {
            view.modifyRenderState({ camera: { far: newValue } });
        }
    };

    const handleClippingCommit = (_event: Event | SyntheticEvent<Element, Event>, newValue: number | number[]) => {
        if (Array.isArray(newValue)) {
            return;
        }

        dispatch(orthoCamActions.setCrossSectionClipping(newValue));
    };

    return (
        <>
            <Box
                sx={{
                    ...(relative
                        ? {}
                        : {
                              position: "absolute",
                              bottom: "36px",
                              translate: "-50% 0",
                              textAlign: "center",
                          }),
                    whiteSpace: "nowrap",
                    px: 1,
                    borderRadius: 1,
                    background: opaqueBackground ? "rgba(255, 255, 255, 0.5)" : undefined,
                }}
            >
                {`${t("clippingName")}: ${convertAndFormat(uom, clipping, UnitType.length)}`}
            </Box>
            <Box
                sx={
                    relative
                        ? {}
                        : {
                              translate: "-50% 0",
                              position: "absolute",
                              bottom: "0px",
                              width: "200px",
                          }
                }
            >
                <Slider
                    getAriaLabel={() => t("clippingName")}
                    value={clipping}
                    min={0.001}
                    max={1}
                    step={0.01}
                    onChange={handleClippingChange}
                    onChangeCommitted={handleClippingCommit}
                    valueLabelDisplay="off"
                />
            </Box>
        </>
    );
}
